/**
 * Modal helpers extracted from `useForm/index.tsx`.
 *
 * Owns the small UI toggles for the form's exit modal, custom listing
 * modal, full-form modal (with backdrop side effects), and the
 * `forceShowListingModal` rule evaluator that maps a field's
 * `meta.forceShowOffers` config to a modal trigger.
 */

import { FieldMetaKeys } from "@lib/shared/types";
import { ActionTypes, ForceShowOffers, FormReducerAction } from "../types";

interface UseFormModalsParams {
    dispatch: (action: FormReducerAction) => void;
    setListingSlug: (slug: string | null) => void;
}

export const useFormModals = ({
    dispatch,
    setListingSlug,
}: UseFormModalsParams) => {
    const toggleShowFormInExitModal = (payload?: boolean) => {
        dispatch({
            type: ActionTypes.TOGGLE_SHOW_FORM_INSIDE_EXIT_MODAL,
            payload: payload,
        });
    };

    const setShowCustomListingModal = (show: boolean) => {
        dispatch({
            type: ActionTypes.SHOW_CUSTOM_LISTING_MODAL,
            payload: { showCustomListingModal: show },
        });
    };

    const toggleFormModal = (payload: boolean) => {
        if (payload) {
            const backdrop = document.getElementById("backdrop");

            if (!backdrop) {
                const backdrop = document.createElement("div");
                backdrop.id = "backdrop";
                backdrop.classList.add("backdrop");
                document.body.appendChild(backdrop);
                document
                    .getElementsByTagName("html")[0]
                    .classList.add("overflow-hidden");
                document.body.classList.add("overflow-hidden");
            }
        } else {
            const backdrop = document.getElementById("backdrop");

            document
                .getElementsByTagName("html")[0]
                .classList.remove("overflow-hidden");
            document.body.classList.remove("overflow-hidden");

            if (backdrop) {
                backdrop.remove();
            }
            dispatch({
                type: ActionTypes.SET_STEP,
                payload: 0,
            });
        }
        dispatch({
            type: ActionTypes.SET_FORM_MODAL,
            payload,
        });
    };

    const forceShowListingModal = ({
        meta,
        selectedValue,
        currentFieldCodeName,
    }: {
        meta: null | { [key in FieldMetaKeys]: string };
        selectedValue: string;
        currentFieldCodeName: string;
    }) => {
        if (meta && meta?.forceShowOffers) {
            const parsedMeta = JSON.parse(
                meta?.forceShowOffers,
            ) as ForceShowOffers;
            if (
                parsedMeta.value === selectedValue &&
                parsedMeta.codeName === currentFieldCodeName
            ) {
                dispatch({
                    type: ActionTypes.SHOW_CUSTOM_LISTING_MODAL,
                    payload: { showCustomListingModal: true },
                });
                setListingSlug(parsedMeta.listSlug);
                return true;
            }
        }
        return false;
    };

    return {
        toggleShowFormInExitModal,
        setShowCustomListingModal,
        toggleFormModal,
        forceShowListingModal,
    };
};
